import { BasicCard } from 'shared/ui/cards/basicCard';
import { DatePickerRange } from 'shared/ui/datepicker';
import { CardContent, FilterPanel, MarginLeftAuto } from './styles';
import { TDaysRange } from 'shared/types';
import { formatISODate, getDateRangeFromToday } from 'shared/lib/datepicker';
import { usePaginationFilter } from 'shared/lib/pagination';
import { Flex } from 'shared/ui/flex';
import { ButtonText } from 'shared/ui/buttons/buttonText';
import { FC, useCallback, useEffect, useRef, useState } from 'react';
import { Filter, resetBookingData, resetOrderDetail } from 'entities/orders';
import guests from 'assets/icons/Guests.svg';
import openBook from 'assets/icons/open-book.svg';

import { FilterButton } from 'shared/ui/pagination';
import { PaginationHook, TNameMapValue } from 'shared/lib/pagination';
import { FilterTags } from './filterTags';
import { useAppDispatch, useAppSelector } from 'shared/lib/store';
import { selectStats } from 'entities/orders/model/selectors';
import { Typography } from 'shared/ui/typography';
import { ExcelDownload } from './excelDownload';

interface FilterManagerProps {
	dateFilter?: { start: string; end: string };
	filterTagsNames: Map<string, TNameMapValue>;
	defaultFilters?: { [key: string]: any };
	showStat?: boolean;
}

export const FilterManager: FC<FilterManagerProps> = ({
	dateFilter,
	filterTagsNames,
	defaultFilters,
	showStat = false
}) => {
	const { fetchDataWithDispatch, resetFilters } =
		usePaginationFilter() as PaginationHook;

	const stats = useAppSelector(selectStats);

	const filter = usePaginationFilter()?.displayFilters as Filter;
	const dispatch = useAppDispatch();

	const [defaultDatesRange, setDefaultDatesRange] = useState<TDaysRange>();
	const isCalledSetDateRange = useRef(false);
	const [resetBtnIsVisible, setVisibleResetBtn] = useState(false);

	const getOrdersByDates = (days: TDaysRange) => {
		if (dateFilter?.start && dateFilter?.end) {
			const daysFrom = days?.from;
			const daysTo = days?.to;

			if (daysFrom && daysTo) {
				fetchDataWithDispatch({
					...filter,
					[dateFilter.start]: formatISODate(daysFrom),
					[dateFilter.end]: formatISODate(daysTo),
					page: 1,
				});

				dispatch(resetOrderDetail());
				dispatch(resetBookingData());
			}
		}
	};

	const setDateRange = useCallback(async () => {
		const [from, to] = getDateRangeFromToday();
		setDefaultDatesRange({
			from,
			to,
		});
	}, []);

	const getOrdersByCurrentDate = useCallback(async () => {
		if (dateFilter?.start && dateFilter?.end) {
			const [today, dayAfterTomorrow] = getDateRangeFromToday(true);

			let params = {
				[dateFilter.start]: today.toISOString(),
				[dateFilter.end]: dayAfterTomorrow.toISOString(),
				page: 1,
			};

			if (defaultFilters) {
				params = {
					...defaultFilters,
					...params,
				};
			}

			fetchDataWithDispatch(params);
		}
	}, [fetchDataWithDispatch, dateFilter, defaultFilters]);

	useEffect(() => {
		if (!isCalledSetDateRange.current) {
			if (dateFilter?.start && dateFilter?.end) {
				setDateRange();
				getOrdersByCurrentDate();
			} else {
				fetchDataWithDispatch();
			}

			isCalledSetDateRange.current = true;
		}
	}, [
		setDateRange,
		getOrdersByCurrentDate,
		fetchDataWithDispatch,
		dateFilter,
	]);

	const resetAllFilters = () => {
		let newFilters: Filter = {};

		if (dateFilter?.start && dateFilter?.end) {
			newFilters = {
				[dateFilter.start]: filter.dt_start__gte,
				[dateFilter.end]: filter.dt_end__lte,
			};
		}

		if (filter.search) {
			newFilters = {
				search: filter.search,
				...newFilters,
			};
		}

		if (defaultFilters) {
			newFilters = { ...newFilters, ...defaultFilters };
		}

		resetFilters({ newFilters, withRequest: true });
	};

	const calcExcludeFiltersCount = useCallback((
		defaultFilter: Filter,
		filter: Filter,
		excludeFiltersCount: number,
	) => {
		if (Array.isArray(filter) && Array.isArray(defaultFilter)) {
			let filtersExistInDefaultCounter = 0;
			filter.map(filterItem => {
				if (defaultFilter.includes(filterItem)) {
					filtersExistInDefaultCounter++;
				}
			});

			if (
				filtersExistInDefaultCounter === defaultFilter.length &&
				filter.length === defaultFilter.length
			) {
				excludeFiltersCount += 1;
			}
		} else if (defaultFilter === filter) {
			excludeFiltersCount += 1;
		}

		return excludeFiltersCount;
	}, []);

	const calcBooleanVisibleReset = useCallback(
		(filter: Filter) => {
			let excludeFiltersCount = dateFilter ? 3 : 1;
			if (filter.search && filter.search !== '') {
				excludeFiltersCount += 1;
			}

			if (filter.search === '') {
				delete filter.search;
			}

			if (defaultFilters) {
				const defaultFiltersKeys = Object.keys(defaultFilters);
				Object.entries(filter).map(([filterKey, filterValue]) => {
					if (defaultFiltersKeys.includes(filterKey)) {
						const defaultFilter = defaultFilters[filterKey];
						excludeFiltersCount = calcExcludeFiltersCount(
							defaultFilter,
							filterValue,
							excludeFiltersCount,
						);
					}
				});
			}

			return Object.keys(filter).length - excludeFiltersCount > 0;
		},
		[dateFilter, defaultFilters, calcExcludeFiltersCount],
	);

	const formatStat = (value: number | string | null) => {
		if (value === 0) {
			return 0;
		}

		if (!value) {
			return 0;
		}

		if (value === 'error') {
			return '—';
		}

		return value.toLocaleString()
	}

	useEffect(() => {
		setVisibleResetBtn(calcBooleanVisibleReset(filter));
	}, [filter, calcBooleanVisibleReset]);

	return (
		<FilterPanel>
			<BasicCard variant={'main'}>
				<CardContent>
					<Flex $alignItems="center" $gap="8px">
						{dateFilter?.start && dateFilter?.end && (
							<DatePickerRange
								selectRange={getOrdersByDates}
								defaultRange={defaultDatesRange}
							/>
						)}
						<Flex $gap="12px" $alignItems="center">
							<FilterButton countExcludeFilters={3} />
							<Flex $wrap="wrap" $gap="8px">
								<FilterTags names={filterTagsNames} />
							</Flex>
						</Flex>
						{resetBtnIsVisible && (
							<ButtonText
								onClick={resetAllFilters}
								padding="5px"
							>
								Сбросить
							</ButtonText>
						)}
            <MarginLeftAuto>
              <ExcelDownload />
            </MarginLeftAuto>
						{showStat && (
              <Flex $gap="16px">
                <Typography
                  icon={{ src: openBook, align: 'center', size: 'medium' }}
                  pointer={true}
                  style={{ minWidth: '24px' }}
                >
                  {formatStat(stats.appointments)}
                </Typography>
                <Typography
                  icon={{ src: guests, align: 'center', size: 'medium' }}
                  pointer={true}
                  style={{ minWidth: '24px' }}
                >
                  {formatStat(stats.participants)}
                </Typography>
              </Flex>
						)}
					</Flex>
				</CardContent>
			</BasicCard>
		</FilterPanel>
	);
};
